import { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { APP_URL } from "../../../constants";
import useFileSelected from "../../../utils/hooks/useFileSelected";
import useInputChange from "../../../utils/hooks/useInputChange";
import { isEmptyObject } from "../../../utils/validators/validators";
import Form from "../../form/Form";
import { useDispatch } from "react-redux/es/exports";
import { updateStaffGuarantorInfo } from "../../../redux/slice/staff/staffSlice";
import AddGuarantor from "../../modals/form/staff/AddGuarantor";
import PhoneInput from "react-phone-number-input";

function GuarantorInfo() {
  const { guarantor_information } = useSelector(
    (state) => state?.staff.staff_information
  );
  const { user_permissions } = useSelector((state) => state.auth?.user);

  const [hasEditStaff, setHasEditStaff] = useState();
  const [editing, setEditing] = useState(false);
  const [selectedId, setSelectedId] = useState(0);
  const [selectedGuarantoInfo, setSelectedGuarantoInfo] = useState({});
  const [error, setError] = useState({});
  const [appId, setAppId] = useState(null);
  const [guarantorInfo, setGuarantorInfo] = useState({});

  const passportRef = useRef();
  const fullnameRef = useRef(null);
  const dispatch = useDispatch();

  // Declare input fields variables
  const [staffInputData, setStaffInputData] = useState({
    fullname: "",
    relationship_with_staff: "",
    years_known_staff: "",
    phone_number: "",
    address: "",
    working_segment: "",
  });

  // Custom hook for input change
  const [input, handleChange] = useInputChange(
    staffInputData,
    setStaffInputData,
    error,
    setError
  );

  // Custom hook for file change
  const [fileInput, fileChangeHandler] = useFileSelected(
    staffInputData,
    setStaffInputData,
    error,
    setError
  );

  const [phoneNumber, setPhoneNumber] = useState("");
  const [borderColor, setBorderColor] = useState("");
  const [errorMessage, setErrorMessage] = useState("");

  const handlePhoneNumberChange = (phone_no) => {
    if (!phone_no) {
      setBorderColor("#fa5c7c");
      setErrorMessage("Phone number is required.");
    } else {
      setBorderColor("");
      setErrorMessage("");
      setPhoneNumber(phone_no);
    }
    setStaffInputData(staffInputData);
  };

  const resetOnClose = () => {
    setEditing(editing ? false : true);
    setBorderColor("");
    setErrorMessage("");
    setPhoneNumber(selectedGuarantoInfo?.phone_number);
    setStaffInputData({
      ...staffInputData,
      fullname: selectedGuarantoInfo?.fullname,
      relationship_with_staff: selectedGuarantoInfo?.relationship_with_staff,
      years_known_staff: selectedGuarantoInfo?.years_known_staff,
      address: selectedGuarantoInfo?.address,
      working_segment: selectedGuarantoInfo?.working_segment,
      id: selectedGuarantoInfo?.id,
      app_id: selectedGuarantoInfo?.app_id,
    });
  };

  const handleSubmit = () => {
    if (isEmptyObject(error)) {
      if (!phoneNumber) {
        setBorderColor("#fa5c7c");
        setErrorMessage("phone number is required.");
        return;
      }
      dispatch(
        updateStaffGuarantorInfo({
          ...staffInputData,
          phone_number: phoneNumber,
        })
      );
    }
  };

  // filter selected information from state
  useEffect(() => {
    setGuarantorInfo(guarantor_information);
    if (guarantor_information?.length > 0) {
      let selectedInfo = guarantor_information.find((f) => f.id == selectedId);
      if (selectedId === 0) {
        selectedInfo = guarantor_information[0];
      }
      setSelectedGuarantoInfo(selectedInfo);
      setAppId(guarantor_information[0].app_id);
      setPhoneNumber(guarantor_information[0]?.phone_number);
    }
  }, [guarantor_information, selectedId]);

  // initialize input fields on select/edit
  useEffect(() => {
    setStaffInputData({
      ...staffInputData,
      fullname: selectedGuarantoInfo?.fullname,
      relationship_with_staff: selectedGuarantoInfo?.relationship_with_staff,
      years_known_staff: selectedGuarantoInfo?.years_known_staff,
      address: selectedGuarantoInfo?.address,
      working_segment: selectedGuarantoInfo?.working_segment,
      id: selectedGuarantoInfo?.id,
      app_id: selectedGuarantoInfo?.app_id,
    });
    setPhoneNumber(selectedGuarantoInfo?.phone_number);
  }, [selectedGuarantoInfo, selectedId]);

  useEffect(() => {
    if (editing) {
      fullnameRef.current.focus();
    }
  }, [editing]);

  useEffect(() => {
    setHasEditStaff(
      user_permissions?.some((permission) => permission.name === "edit staff")
    );
  }, [user_permissions]);

  return (
    <div className='border p-4 rounded-3 mt-4 px-3 position-relative'>
      <h4 className='fw-bolder text-primary bg-white position-absolute profile-title'>
        Guarantor Information
      </h4>
      {guarantor_information && guarantor_information.length > 0 ? (
        <>
          <Form
            setError={(e) => {
              if (!phoneNumber) {
                setBorderColor("#fa5c7c");
                setErrorMessage("phone number is required.");
              }
              setError(e);
            }}
            submitHandler={handleSubmit}
            values={staffInputData}
          >
            <div className='row shadow p-3 '>
              <div className='row p-3 border border-top-0 border-start-0 border-bottom-1 border-end-0'>
                <div className='col-xs-6 col-sm-6 col-md-3'>
                  <h5>Full Names</h5>
                  {!editing && (
                    <p>
                      {selectedId === 0
                        ? guarantor_information[0].fullname
                        : selectedGuarantoInfo.fullname}
                    </p>
                  )}
                  {editing && (
                    <input
                      className={`form-control update-input ${
                        error.fullname && "is-invalid"
                      }`}
                      min='2'
                      max='255'
                      textname='fullname'
                      type='text'
                      name='fullname'
                      value={input.fullname}
                      onChange={handleChange}
                      id='fullname'
                      ref={fullnameRef}
                    />
                  )}
                  {error.fullname && (
                    <span className='invalid-feedback'>{error.fullname}</span>
                  )}
                </div>
                <div className='col-xs-6 col-sm-6 col-md-3'>
                  <h5>Relationship With Staff</h5>
                  {!editing && (
                    <p>
                      {selectedId === 0
                        ? guarantor_information[0].relationship_with_staff
                        : selectedGuarantoInfo.relationship_with_staff}
                    </p>
                  )}
                  {editing && (
                    <input
                      className={`form-control update-input ${
                        error.relationship_with_staff && "is-invalid"
                      }`}
                      min='5'
                      max='15'
                      textname='relationship with staff'
                      type='text'
                      name='relationship_with_staff'
                      value={input.relationship_with_staff}
                      onChange={handleChange}
                      id='relationship_with_staff'
                    />
                  )}
                  {error.relationship_with_staff && (
                    <span className='invalid-feedback'>
                      {error.relationship_with_staff}
                    </span>
                  )}
                </div>
                <div className='col-xs-6 col-sm-6 col-md-3'>
                  <h5>Years In Relationship</h5>
                  {!editing && (
                    <p>
                      {selectedId === 0
                        ? guarantor_information[0].years_known_staff
                        : selectedGuarantoInfo.years_known_staff}
                    </p>
                  )}
                  {editing && (
                    <input
                      className={`form-control update-input ${
                        error.years_known_staff && "is-invalid"
                      }`}
                      min='1'
                      textname='years known staff'
                      type='text'
                      name='years_known_staff'
                      value={input.years_known_staff}
                      onChange={handleChange}
                      id='years_known_staff'
                    />
                  )}
                  {error.years_known_staff && (
                    <span className='invalid-feedback'>
                      {error.years_known_staff}
                    </span>
                  )}
                </div>
                <div className='col-xs-6 col-sm-6 col-md-3'>
                  <h5>Phone Number</h5>
                  {!editing && (
                    <p>
                      {selectedId === 0
                        ? guarantor_information[0].phone_number
                        : selectedGuarantoInfo.phone_number}
                    </p>
                  )}
                  {editing && (
                    <div className='custom-prefix position-relative'>
                      <PhoneInput
                        placeholder='Enter phone number'
                        value={phoneNumber}
                        onChange={handlePhoneNumberChange}
                        onBlur={(e) => {
                          if (!e.target.value) {
                            setBorderColor("#fa5c7c");
                            setErrorMessage("Phone number is required.");
                          } else {
                            setBorderColor("");
                            setErrorMessage("");
                          }
                        }}
                        defaultCountry='NG'
                        className='form-control custom-phone-input'
                        style={{ borderColor: `${borderColor}` }}
                      />
                      {errorMessage && (
                        <span
                          className='text-danger text-sm'
                          style={{ fontSize: "12px" }}
                        >
                          {errorMessage}
                        </span>
                      )}
                    </div>
                  )}
                </div>
              </div>
              <div className='row p-3'>
                <div className='col-sm-6 col-md-4'>
                  <h5>Contact Address</h5>
                  {!editing && (
                    <p>
                      {selectedId === 0
                        ? guarantor_information[0].address
                        : selectedGuarantoInfo.address}
                    </p>
                  )}

                  {editing && (
                    <textarea
                      className={`form-control update-input ${
                        error.address && "is-invalid"
                      }`}
                      min='1'
                      textname='address'
                      type='text'
                      name='address'
                      value={input.address}
                      onChange={handleChange}
                      id='address'
                      cols='70'
                      rows='3'
                    ></textarea>
                  )}
                  {error.address && (
                    <span className='invalid-feedback'>{error.address}</span>
                  )}
                </div>
                <div className='col-sm-6 col-md-4'>
                  <h5>Working Segment</h5>
                  {!editing && (
                    <p>
                      {selectedId === 0
                        ? guarantor_information[0].working_segment
                        : selectedGuarantoInfo.working_segment}
                    </p>
                  )}
                  {editing && (
                    <input
                      className={`form-control update-input ${
                        error.working_segment && "is-invalid"
                      }`}
                      min='1'
                      textname='working segment'
                      type='text'
                      name='working_segment'
                      value={input.working_segment}
                      onChange={handleChange}
                      id='working_segment'
                    />
                  )}
                  {error.working_segment && (
                    <span className='invalid-feedback'>
                      {error.working_segment}
                    </span>
                  )}
                </div>
                <div className='col-sm-12 col-md-4 mt-3'>
                  <div className='profile-picture-container'>
                    <div>
                      <div className='d-flex mt-2 justify-content-cente'>
                        <input
                          textname='passport'
                          className='d-none'
                          accept='image/*'
                          type='file'
                          name='image'
                          // value={input?.image}
                          onChange={fileChangeHandler}
                          ref={passportRef}
                        />

                        {editing ? (
                          <img
                            className='profile-picture mx-auto img-responsive'
                            src={`${
                              editing && staffInputData.temp_url
                                ? staffInputData.temp_url
                                : selectedGuarantoInfo?.image?.includes(
                                    "res.cloudinary"
                                  )
                                ? selectedGuarantoInfo?.image
                                : APP_URL + selectedGuarantoInfo?.image
                            }`}
                            alt='profile'
                          />
                        ) : (
                          <img
                            className='profile-picture mx-auto img-responsive'
                            src={`${
                              selectedGuarantoInfo.image
                                ? selectedGuarantoInfo?.image?.includes(
                                    "res.cloudinary"
                                  )
                                  ? selectedGuarantoInfo?.image
                                  : APP_URL + selectedGuarantoInfo.image
                                : APP_URL + guarantor_information[0]?.image
                            }`}
                            alt='profile'
                          />
                        )}
                        {/* <img className='profile-picture mx-auto img-responsive' 
                                src={`${editing && staffInputData.temp_url ? staffInputData.temp_url : APP_URL+guarantor_information[0]?.image }`} alt="profile" /> */}
                        {/* || APP_URL+guarantor_information[0]?.image */}
                      </div>
                      <div className='d-flex justify-content-center'>
                        <div>
                          {editing && (
                            <span
                              className='btn btn-sm btn-secondary mt-2 px-3'
                              onClick={() => passportRef.current.click()}
                            >
                              Browse
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                {hasEditStaff && (
                  <div className='row mt-4'>
                    <div className='d-flex justify-content-end'>
                      <div>
                        <a
                          className={`btn btn-${
                            editing ? "warning" : "info"
                          } btn-sm mt-3 px-4 text-white`}
                          onClick={() => {
                            resetOnClose();
                            // setError({...{}, })
                          }}
                        >
                          {editing ? "Close" : "Edit"}
                        </a>
                        {editing && (
                          <button className='btn btn-success ms-2 btn-sm mt-3 px-4'>
                            Save
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
            <hr />

            <div className='col-sm-12 table-responsive'>
              <table
                className='table table-centered table-striped dt-responsive nowrap w-100 dataTable no-footer dtr-inline collapsed'
                id='products-datatable'
                aria-describedby='products-datatable_info'
              >
                <thead>
                  <tr>
                    <th style={{ width: "27.6px" }}>#</th>
                    <th style={{ width: "117.8px" }}>Full Name</th>
                    <th style={{ width: "106.8px" }}>Phone Number</th>
                    <th style={{ width: "106.8px" }}>Work Segment</th>
                    <th style={{ width: "96.8px" }}>Relationship With Staff</th>
                    <th style={{ width: "75.6px" }}>Years In Relationship</th>
                    <th style={{ width: "55.8px" }}>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {guarantor_information.map(
                    (
                      {
                        id,
                        fullname,
                        phone_number,
                        working_segment,
                        years_known_staff,
                        relationship_with_staff,
                      },
                      index
                    ) => (
                      <tr key={id}>
                        <td>{index + 1}</td>
                        <td>{fullname}</td>
                        <td>{phone_number}</td>
                        <td>{working_segment}</td>
                        <td>{relationship_with_staff}</td>
                        <td className='link-'>{years_known_staff}</td>
                        <td className='text-danger'>
                          {hasEditStaff && (
                            <span
                              style={{ cursor: "pointer" }}
                              onClick={() => {
                                setSelectedId(id);
                                setEditing(true);
                              }}
                            >
                              <i className='mdi mdi-pencil text-success fs-4 me-2 '></i>
                            </span>
                          )}
                          <span onClick={() => setSelectedId(id)}>
                            <i className='mdi mdi-eye text-info fs-4 me-2 '></i>
                          </span>
                          {/* {index > 0 && <span style={{cursor: 'pointer'}}><i className='uil uil-times text-danger fs-4'></i></span>} */}
                        </td>
                      </tr>
                    )
                  )}
                </tbody>
              </table>
            </div>
          </Form>
          {hasEditStaff && (
            <div className='d-flex float-end '>
              <div>
                <button
                  data-bs-toggle='modal'
                  data-bs-target='#add_guarantor_modal'
                  className='btn btn-primary btn-sm mt-3'
                >
                  <span className='text-white'>
                    <i className='uil-user-plus mr-2'></i>{" "}
                    <span>Add Guarantor</span>{" "}
                  </span>
                </button>
                <AddGuarantor appId={appId} id='add_guarantor_modal' />
              </div>
            </div>
          )}
          <div className='clearfix'></div>
        </>
      ) : (
        <div className='alert alert-info'>No Record Found</div>
      )}
    </div>
  );
}

export default GuarantorInfo;
